import { ReactNode, useState } from 'react';
import { Box, alpha } from '@mui/material';

/** La caja de imagen del arquetipo de producto. */
export const RESULT_MEDIA_WIDTH = 112;
export const RESULT_MEDIA_HEIGHT = 84;

interface Props {
  /**
   * Foto del producto (vehículo, experiencia, destino): LLENA la caja. Es
   * argumento de venta, así que se recorta antes que dejar aire alrededor.
   */
  photo?: string;
  /**
   * Marca del proveedor: respira dentro de la caja y nunca se recorta — un
   * logo cortado deja de identificar a nadie.
   */
  logo?: string;
  /** Lo que se dibuja sin imagen. También es el respaldo si la imagen falla. */
  icon: ReactNode;
  /** Texto alternativo de la imagen (el ícono es decorativo). */
  alt?: string;
}

/**
 * La única definición de la caja de media de los resultados. Antes vivía
 * copiada en cada vertical con medidas y `object-fit` distintos, así que dos
 * ofertas de la misma búsqueda no se veían igual.
 *
 * Una URL que no carga SIEMPRE cae al ícono del vertical: el ícono roto del
 * navegador donde va la foto del producto es peor que no tener foto.
 */
export function ResultMedia({ photo, logo, icon, alt = '' }: Props) {
  const [failed, setFailed] = useState(false);

  const source = photo ?? logo;
  const showImage = Boolean(source) && !failed;

  return (
    <Box
      sx={(theme) => ({
        width: { xs: '100%', sm: RESULT_MEDIA_WIDTH },
        maxWidth: { xs: 240, sm: RESULT_MEDIA_WIDTH },
        height: RESULT_MEDIA_HEIGHT,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
        // Del theme y no un gris fijo: cada tenant tiene su branding.
        bgcolor: alpha(theme.palette.primary.main, 0.06),
      })}
    >
      {showImage ? (
        <Box
          component="img"
          src={source}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
          sx={
            photo
              ? { width: '100%', height: '100%', objectFit: 'cover' }
              : { maxWidth: '78%', maxHeight: 48, objectFit: 'contain' }
          }
        />
      ) : (
        icon
      )}
    </Box>
  );
}
